<template>
  <div 
    class="zoomable-container"
    @touchstart="handleTouchStart"
    @touchmove="handleTouchMove"
    @touchend="handleTouchEnd"
    @wheel="handleWheel"
    :style="containerStyle"
  >
    <div 
      class="zoomable-content"
      :style="contentStyle"
    >
      <slot></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ZoomableContent',
  props: {
    minScale: {
      type: Number,
      default: 0.5
    },
    maxScale: {
      type: Number,
      default: 3
    },
    initialScale: {
      type: Number,
      default: 1
    },
    autoFit: {
      type: Boolean,
      default: true
    },
    fitScale: {
      type: Number,
      default: 0.8
    }
  },
  data() {
    return {
      scale: this.initialScale,
      translateX: 0,
      translateY: 0,
      lastTouchDistance: 0,
      lastTouchCenter: { x: 0, y: 0 },
      isDragging: false,
      lastTouchTime: 0,
      touches: [],
      isInitialized: false,
      calculateTimer: null,
      lastTapTime: 0,
      tapCount: 0
    }
  },
  computed: {
    containerStyle() {
      return {
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
        height: '100%',
        touchAction: 'none'
      }
    },
    contentStyle() {
      return {
        transform: `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`,
        transformOrigin: 'center center',
        transition: this.isDragging ? 'none' : 'transform 0.3s ease-out',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }
    }
  },
  mounted() {
    // 确保每个实例都有独立的初始状态
    this.resetToInitialState();
    
    this.$nextTick(() => {
      if (this.autoFit) {
        this.calculateFitScale();
      } else {
        // 如果不使用autoFit，设置初始缩放
        this.scale = this.initialScale;
        this.translateX = 0;
        this.translateY = 0;
        this.isInitialized = true;
      }
    });
  },
  watch: {
    initialScale(newVal) {
      if (!this.isInitialized) {
        this.scale = newVal;
        this.translateX = 0;
        this.translateY = 0;
      }
    },
    
    // 监听关键 props 的变化，确保组件状态正确重置
    minScale(newVal) {
      if (this.scale < newVal) {
        this.scale = newVal;
      }
    },
    
    maxScale(newVal) {
      if (this.scale > newVal) {
        this.scale = newVal;
      }
    },
    
    // 当 autoFit 或 fitScale 改变时，重新计算
    autoFit() {
      this.recalculate();
    },
    
    fitScale() {
      if (this.autoFit) {
        this.recalculate();
      }
    }
  },
  methods: {
    handleTouchStart(event) {
      event.preventDefault();
      this.touches = Array.from(event.touches);
      this.isDragging = true;
      this.lastTouchTime = Date.now();

      if (this.touches.length === 2) {
        // 双指操作
        this.lastTouchDistance = this.getTouchDistance(this.touches[0], this.touches[1]);
        this.lastTouchCenter = this.getTouchCenter(this.touches[0], this.touches[1]);
      } else if (this.touches.length === 1) {
        // 单指操作
        this.lastTouchCenter = {
          x: this.touches[0].clientX,
          y: this.touches[0].clientY
        };
      }
    },

    handleTouchMove(event) {
      event.preventDefault();
      if (!this.isDragging) return;

      const currentTouches = Array.from(event.touches);

      if (currentTouches.length === 2 && this.touches.length === 2) {
        // 双指缩放
        const currentDistance = this.getTouchDistance(currentTouches[0], currentTouches[1]);
        const currentCenter = this.getTouchCenter(currentTouches[0], currentTouches[1]);
        
        // 计算缩放比例
        const scaleChange = currentDistance / this.lastTouchDistance;
        const newScale = Math.max(this.minScale, Math.min(this.maxScale, this.scale * scaleChange));
        
        if (newScale !== this.scale) {
          // 获取容器的边界信息
          const rect = this.$el.getBoundingClientRect();
          
          // 使用当前触摸中心作为缩放焦点
          const centerX = currentCenter.x - rect.left;
          const centerY = currentCenter.y - rect.top;
          
          // 保存当前状态
          const oldScale = this.scale;
          const oldTranslateX = this.translateX;
          const oldTranslateY = this.translateY;
          
          // 计算焦点在内容中的相对位置（0-1之间）
          const contentElement = this.$el.querySelector('.zoomable-content');
          const contentRect = contentElement.getBoundingClientRect();
          const contentCenterX = contentRect.left + contentRect.width / 2 - rect.left;
          const contentCenterY = contentRect.top + contentRect.height / 2 - rect.top;
          
          // 计算焦点相对于内容中心的偏移
          const focusOffsetX = centerX - contentCenterX;
          const focusOffsetY = centerY - contentCenterY;
          
          // 更新缩放
          this.scale = newScale;
          
          // 计算新的位移，保持焦点位置不变
          const scaleRatio = newScale / oldScale;
          this.translateX = oldTranslateX + focusOffsetX * (1 - scaleRatio);
          this.translateY = oldTranslateY + focusOffsetY * (1 - scaleRatio);
        }
        
        // 更新记录的值
        this.lastTouchDistance = currentDistance;
        this.lastTouchCenter = currentCenter;
      } else if (currentTouches.length === 1 && this.touches.length === 1) {
        // 单指拖拽
        const deltaX = currentTouches[0].clientX - this.lastTouchCenter.x;
        const deltaY = currentTouches[0].clientY - this.lastTouchCenter.y;
        
        this.translateX += deltaX;
        this.translateY += deltaY;
        
        this.lastTouchCenter = {
          x: currentTouches[0].clientX,
          y: currentTouches[0].clientY
        };
      }

      this.touches = currentTouches;
    },

    handleTouchEnd(event) {
      event.preventDefault();
      
      const now = Date.now();
      
      // 更严格的双击检测 - 只在单指快速点击时触发
      if (event.touches.length === 0 && this.touches.length <= 1) {
        const touchDuration = now - this.lastTouchTime;
        
        // 只有快速点击（不是长按拖拽）才考虑双击
        if (touchDuration < 200) {
          if (now - this.lastTapTime < 500) {
            this.tapCount++;
          } else {
            this.tapCount = 1;
          }
          
          this.lastTapTime = now;
          
          // 只有连续两次快速点击才算双击
          if (this.tapCount === 2) {
            this.handleDoubleTap();
            this.tapCount = 0; // 重置计数
          } else {
            // 单击后延迟重置计数，避免误触
            setTimeout(() => {
              if (this.tapCount === 1) {
                this.tapCount = 0;
              }
            }, 500);
          }
        } else {
          // 长按拖拽后重置计数
          this.tapCount = 0;
        }
      }
      
      this.isDragging = false;
      this.touches = [];
      
      // 边界检查和回弹 - 暂时禁用以测试拖拽问题
      // this.$nextTick(() => {
      //   this.constrainPosition();
      // });
    },

    handleWheel(event) {
      event.preventDefault();
      
      const delta = event.deltaY > 0 ? 0.9 : 1.1;
      const newScale = Math.max(this.minScale, Math.min(this.maxScale, this.scale * delta));
      
      // 获取容器的边界信息
      const rect = this.$el.getBoundingClientRect();
      
      // 计算鼠标位置相对于容器的坐标
      const centerX = event.clientX - rect.left;
      const centerY = event.clientY - rect.top;
      
      // 计算缩放前内容在鼠标点的坐标（相对于内容的原始坐标系）
      const contentX = (centerX - this.translateX) / this.scale;
      const contentY = (centerY - this.translateY) / this.scale;
      
      // 更新缩放
      this.scale = newScale;
      
      // 计算缩放后该点应该在的位置
      const newContentX = contentX * this.scale;
      const newContentY = contentY * this.scale;
      
      // 计算新的位移，使该点保持在鼠标位置
      this.translateX = centerX - newContentX;
      this.translateY = centerY - newContentY;
      
      // 暂时禁用边界约束以测试拖拽问题
      // this.$nextTick(() => {
      //   this.constrainPosition();
      // });
    },

    handleDoubleTap() {
      // 双击在当前计算出的合适缩放和最大缩放之间切换
      const targetScale = this.getTargetScale();
      
      if (Math.abs(this.scale - targetScale) < 0.1) {
        // 如果当前是合适缩放，放大到最大倍率
        this.scale = this.maxScale;
      } else {
        // 否则回到合适缩放
        this.scale = targetScale;
        this.translateX = 0;
        this.translateY = 0;
      }
    },

    // 获取当前内容的目标缩放比例
    getTargetScale() {
      const contentElement = this.$el.querySelector('.zoomable-content > *');
      if (!contentElement) return this.fitScale;

      const containerRect = this.$el.getBoundingClientRect();
      let contentWidth, contentHeight;
      
      if (contentElement.tagName === 'IMG') {
        contentWidth = contentElement.naturalWidth || contentElement.width;
        contentHeight = contentElement.naturalHeight || contentElement.height;
      } else if (contentElement.tagName === 'CANVAS') {
        contentWidth = contentElement.width;
        contentHeight = contentElement.height;
      } else {
        const contentRect = contentElement.getBoundingClientRect();
        contentWidth = contentRect.width;
        contentHeight = contentRect.height;
      }

      if (containerRect.width === 0 || containerRect.height === 0 || 
          contentWidth === 0 || contentHeight === 0) {
        return this.fitScale;
      }

      const padding = 150;
      const availableWidth = containerRect.width - padding * 2;
      const availableHeight = containerRect.height - padding * 2;
      const scaleX = availableWidth / contentWidth;
      const scaleY = availableHeight / contentHeight;
      const baseFitScale = Math.min(scaleX, scaleY, 1);
      
      let scaleFactor = this.fitScale;
      if (contentElement.tagName === 'CANVAS' && 
          contentWidth === 375 && contentHeight === 523) {
        scaleFactor = 3.5;
      }
      
      return Math.max(this.minScale, Math.min(this.maxScale, baseFitScale * scaleFactor));
    },

    getTouchDistance(touch1, touch2) {
      const dx = touch1.clientX - touch2.clientX;
      const dy = touch1.clientY - touch2.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    },

    getTouchCenter(touch1, touch2) {
      return {
        x: (touch1.clientX + touch2.clientX) / 2,
        y: (touch1.clientY + touch2.clientY) / 2
      };
    },

    constrainPosition() {
      // 对于缩放小于等于1的情况，允许一定的移动自由度
      if (this.scale <= 1) {
        // 限制移动范围，但不完全重置到中心
        const maxMove = 50; // 允许50px的移动范围
        this.translateX = Math.max(-maxMove, Math.min(maxMove, this.translateX));
        this.translateY = Math.max(-maxMove, Math.min(maxMove, this.translateY));
        return;
      }

      // 获取容器的尺寸
      const containerRect = this.$el.getBoundingClientRect();
      
      // 获取实际内容元素的尺寸
      const contentElement = this.$el.querySelector('.zoomable-content > *');
      if (!contentElement) return;
      
      // 获取内容的原始尺寸
      let originalWidth, originalHeight;
      if (contentElement.tagName === 'IMG') {
        originalWidth = contentElement.naturalWidth || contentElement.width;
        originalHeight = contentElement.naturalHeight || contentElement.height;
      } else if (contentElement.tagName === 'CANVAS') {
        originalWidth = contentElement.width;
        originalHeight = contentElement.height;
      } else {
        const style = window.getComputedStyle(contentElement);
        originalWidth = parseFloat(style.width);
        originalHeight = parseFloat(style.height);
      }
      
      // 如果无法获取尺寸，不进行约束
      if (!originalWidth || !originalHeight) return;
      
      // 计算缩放后的尺寸
      const scaledWidth = originalWidth * this.scale;
      const scaledHeight = originalHeight * this.scale;
      
      // 计算最大允许的位移（内容超出容器的部分）
      const maxTranslateX = Math.max(0, (scaledWidth - containerRect.width) / 2);
      const maxTranslateY = Math.max(0, (scaledHeight - containerRect.height) / 2);
      
      // 给予更大的移动缓冲区，特别是对于大尺寸内容
      let buffer = 50; // 基础缓冲区
      
      // 对于点赞canvas（375x523）给予更大的缓冲区
      if (contentElement.tagName === 'CANVAS' && 
          originalWidth === 375 && originalHeight === 523) {
        buffer = 100; // 更大的缓冲区
      }
      
      // 对于很大的内容，给予更宽松的约束
      if (scaledWidth > containerRect.width * 2 || scaledHeight > containerRect.height * 2) {
        buffer = Math.max(buffer, 100);
      }
      
      // 约束位移范围
      this.translateX = Math.max(-maxTranslateX - buffer, Math.min(maxTranslateX + buffer, this.translateX));
      this.translateY = Math.max(-maxTranslateY - buffer, Math.min(maxTranslateY + buffer, this.translateY));
    },

    // 计算合适的缩放比例
    calculateFitScale(retryCount = 0) {
      // 清除之前的定时器
      if (this.calculateTimer) {
        clearTimeout(this.calculateTimer);
        this.calculateTimer = null;
      }
      
      const maxRetries = 20; // 最多重试20次
      
      if (retryCount >= maxRetries) {
        console.warn('ZoomableContent: 达到最大重试次数，使用默认缩放');
        this.scale = this.fitScale;
        this.translateX = 0;
        this.translateY = 0;
        this.isInitialized = true;
        return;
      }

      const contentElement = this.$el.querySelector('.zoomable-content > *');
      if (!contentElement) {
        console.log('ZoomableContent: 内容元素未找到，重试...', retryCount + 1);
        setTimeout(() => this.calculateFitScale(retryCount + 1), 100);
        return;
      }

      const containerRect = this.$el.getBoundingClientRect();
      
      // 对于图片，使用naturalWidth/naturalHeight
      let contentWidth, contentHeight;
      if (contentElement.tagName === 'IMG') {
        // 等待图片加载完成
        if (!contentElement.complete || contentElement.naturalWidth === 0) {
          console.log('ZoomableContent: 图片未加载完成，重试...', retryCount + 1);
          setTimeout(() => this.calculateFitScale(retryCount + 1), 100);
          return;
        }
        contentWidth = contentElement.naturalWidth;
        contentHeight = contentElement.naturalHeight;
      } else if (contentElement.tagName === 'CANVAS') {
        contentWidth = contentElement.width;
        contentHeight = contentElement.height;
      } else {
        const contentRect = contentElement.getBoundingClientRect();
        contentWidth = contentRect.width;
        contentHeight = contentRect.height;
      }

      // 检查容器尺寸，如果为0则等待更长时间
      if (containerRect.width === 0 || containerRect.height === 0) {
        console.log('ZoomableContent: 容器尺寸为0，重试...', {
          retryCount: retryCount + 1,
          containerWidth: containerRect.width,
          containerHeight: containerRect.height,
          contentWidth,
          contentHeight
        });
        // 容器尺寸为0时，等待更长时间
        const delay = retryCount < 5 ? 200 : 500;
        this.calculateTimer = setTimeout(() => this.calculateFitScale(retryCount + 1), delay);
        return;
      }

      if (contentWidth === 0 || contentHeight === 0) {
        console.log('ZoomableContent: 内容尺寸为0，重试...', retryCount + 1);
        setTimeout(() => this.calculateFitScale(retryCount + 1), 100);
        return;
      }

      // 计算适合容器的缩放比例，留出更多边距让图片显示更小
      const padding = 150; // 增加到150px边距
      const availableWidth = containerRect.width - padding * 2;
      const availableHeight = containerRect.height - padding * 2;
      
      const scaleX = availableWidth / contentWidth;
      const scaleY = availableHeight / contentHeight;
      
      // 选择较小的缩放比例，并应用自定义的缩放因子
      const baseFitScale = Math.min(scaleX, scaleY, 1);
      
      // 检测点赞canvas并设置特殊缩放
      let scaleFactor = this.fitScale;
      if (contentElement.tagName === 'CANVAS' && 
          contentWidth === 375 && contentHeight === 523) {
        scaleFactor = 3.5; // 设置为3.5倍，稍微大一点
        console.log('ZoomableContent: 【新版本】检测到点赞canvas，使用3.5倍缩放', {
          scaleFactor,
          baseFitScale,
          finalScale: baseFitScale * scaleFactor,
          containerSize: `${containerRect.width}x${containerRect.height}`,
          canvasSize: `${contentWidth}x${contentHeight}`
        });
      }
      
      const fitScale = baseFitScale * scaleFactor;
      
      console.log('ZoomableContent: 计算缩放比例成功', {
        retryCount,
        contentType: contentElement.tagName,
        contentSize: `${contentWidth}x${contentHeight}`,
        containerSize: `${containerRect.width}x${containerRect.height}`,
        baseFitScale,
        scaleFactor,
        finalScale: fitScale,
        fitScaleProp: this.fitScale
      });
      
      // 应用计算出的缩放比例
      this.scale = Math.max(this.minScale, Math.min(this.maxScale, fitScale));
      this.translateX = 0;
      this.translateY = 0;
      this.isInitialized = true;
    },

    // 重新计算并应用合适的缩放比例
    recalculate() {
      // 完全重置到初始状态
      this.resetToInitialState();
      
      // 等待DOM更新后重新计算
      this.$nextTick(() => {
        // 再次等待，确保内容元素已经渲染
        setTimeout(() => {
          if (this.autoFit) {
            this.calculateFitScale();
          } else {
            this.scale = this.initialScale;
            this.translateX = 0;
            this.translateY = 0;
            this.isInitialized = true;
          }
        }, 50);
      });
    },

    // 重置缩放和位置
    reset() {
      this.resetToInitialState();
    },

    // 重置到初始状态的统一方法
    resetToInitialState() {
      // 完全重置所有状态到初始值
      this.scale = this.minScale;
      this.translateX = 0;
      this.translateY = 0;
      this.isInitialized = false;
      this.isDragging = false;
      this.touches = [];
      this.lastTouchDistance = 0;
      this.lastTouchCenter = { x: 0, y: 0 };
      this.lastTouchTime = 0;
      this.lastTapTime = 0;
      this.tapCount = 0;
      
      // 清除任何待执行的计算
      if (this.calculateTimer) {
        clearTimeout(this.calculateTimer);
        this.calculateTimer = null;
      }
    }
  }
}
</script>

<style scoped>
.zoomable-container {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  position: relative;
  width: 100%;
  height: 100%;
}

.zoomable-content {
  cursor: grab;
  width: 100%;
  height: 100%;
}

.zoomable-content:active {
  cursor: grabbing;
}

/* 确保内容元素能够自由缩放 */
.zoomable-content > img,
.zoomable-content > canvas {
  display: block;
}
</style>